/**
 * Sentry `beforeSend` / `beforeBreadcrumb` scrubber. Walks every
 * payload before it leaves the device and redacts any value whose key
 * name matches a known secret / PII pattern.
 *
 * The list lives in one place (this file) so the backend scrubber
 * (`backend/src/sentry/scrubber.ts`) can stay in sync by mirroring the
 * same names. If you add a new secret-carrying field anywhere in the
 * app, add its name here too.
 *
 * Matching rules:
 *   - Case-insensitive
 *   - Substring match — so `passwordHash`, `userPassword`, and
 *     `data.body.password` all scrub
 *   - Recursive through objects + arrays
 *
 * Redaction: the value is replaced with the string "[REDACTED]". We do
 * NOT preserve the type (e.g., turn a number into the same-shaped
 * fake number) — Sentry's UI is fine with strings in place of other
 * types and the explicit "[REDACTED]" marker is reviewable.
 */

const SECRET_FIELD_PATTERNS = [
  'password',
  'passcode',
  'secret',
  'token',
  'authorization',
  'cookie',
  'privatekey',
  'private_key',
  'seedphrase',
  'seed_phrase',
  'mnemonic',
  'accountnumber',
  'account_number',
  'routingnumber',
  'routing_number',
  'credentials',
  'licensenumber',
  'license_number',
  'validationanswer',
  'validation_answer',
  'masterkey',
  'master_key',
  'wrappedkey',
  'wrapped_key',
  'salt',
  'iv',
  'ciphertext',
  'verifier',
  'ssn',
  'socialsecurity',
  'social_security',
  'dateofbirth',
  'date_of_birth',
  'dob',
  'cvv',
  'pin',
] as const;

const REDACTED = '[REDACTED]';

// Guard against pathological payloads — a cycle or deep nest shouldn't
// take the scrubber offline. 200 is far deeper than any realistic
// Sentry event.
const MAX_DEPTH = 50;

export function isSecretFieldName(name: string): boolean {
  if (typeof name !== 'string' || name.length === 0) return false;
  const lower = name.toLowerCase();
  for (const pat of SECRET_FIELD_PATTERNS) {
    if (lower.includes(pat)) return true;
  }
  return false;
}

function scrubValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (depth > MAX_DEPTH) return REDACTED;
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return value;
  if (seen.has(value as object)) return '[Circular]';
  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, depth + 1, seen));
  }

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (isSecretFieldName(k)) {
      out[k] = REDACTED;
    } else {
      out[k] = scrubValue(v, depth + 1, seen);
    }
  }
  return out;
}

/**
 * Walk an object (Sentry event payload, breadcrumb data, extra, tags)
 * and redact any key whose name matches a secret pattern. Returns a
 * NEW object; does not mutate input.
 */
export function scrubSensitiveFields<T>(input: T): T {
  if (input === null || input === undefined) return input;
  if (typeof input !== 'object') return input;
  return scrubValue(input, 0, new WeakSet()) as T;
}

/**
 * Exported for tests + for the backend to mirror the same list.
 */
export const __internal = {
  SECRET_FIELD_PATTERNS,
  REDACTED,
  MAX_DEPTH,
};
